import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiEye } from 'react-icons/fi';
import { heroData } from '../data/hero';
import styles from '../styles/Hero.module.css';

const Hero = ({ onProjectsClick }) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);


  // Other variants...
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.34, 1.56, 0.64, 1],
      },
    },
  };

  const buttonContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.6,
      },
    },
  };

  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section className={styles.hero} id="hero" ref={containerRef}>

      {/* Spotlight Effect */}
      <div 
        className={styles.hero__spotlight}
        style={{
          background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(99, 102, 241, 0.1), transparent 40%)`,
        }}
      />

      {/* Decorative accent lines */}
      <motion.div
        className={`${styles.hero__accent_line} ${styles.hero__line_top}`}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.5, ease: 'easeOut' }}
        style={{ originX: 0 }}
      />

      <motion.div
        className={styles.hero__container}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Column - Text Content */}
        <div className={styles.hero__text_col}>
          <motion.h1
            className={styles.hero__name}
            variants={itemVariants}
          >
            {heroData.name}
          </motion.h1>

          <motion.p className={styles.hero__title} variants={itemVariants}>
            {heroData.title}
          </motion.p>

          <motion.p className={styles.hero__summary} variants={itemVariants}>
            {heroData.summary}
          </motion.p>

          {/* Call-to-Action Buttons */}
          <motion.div
            className={styles.hero__ctas}
            variants={buttonContainerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.button
              className={`${styles.hero__cta_button} ${styles['hero__cta-button--primary']}`}
              onClick={onProjectsClick}
              aria-label="View my projects"
              variants={buttonVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {heroData.ctas.primary.label}
              <FiArrowRight size={18} />
            </motion.button>

            <motion.a
              href={heroData.ctas.secondary.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.hero__cta_button} ${styles['hero__cta-button--secondary']}`}
              aria-label={heroData.ctas.secondary.ariaLabel}
              variants={buttonVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {heroData.ctas.secondary.label}
              <FiEye size={18} />
            </motion.a>
          </motion.div>
        </div>

        {/* Right Column - Visual/Decorative */}
        <motion.div 
          className={styles.hero__visual_col}
          variants={itemVariants}
        >
          <div className={styles.hero__profile_card}>
            {/* Terminal Header */}
            <div className={styles.profile_card__header}>
              <div className={styles.profile_card__dots}>
                <span style={{background: '#ff5f56'}}></span>
                <span style={{background: '#ffbd2e'}}></span>
                <span style={{background: '#27c93f'}}></span>
              </div>
              <span className={styles.profile_card__title}>{heroData.profileCardTitle}</span>
            </div>

            {/* Quick Stats */}
            <div className={styles.hero__stats_grid}>
              {heroData.stats.map((stat, index) => (
                <div key={index} className={styles.hero__stat_item}>
                  <span className={styles.hero__stat_value}>{stat.value}</span>
                  <span className={styles.hero__stat_label}>{stat.label}</span>
                </div>
              ))}
            </div>

            {/* Focus Areas */}
            <div className={styles.hero__focus_section}>
              <span className={styles.hero__focus_label}>{'//'} What I am good at</span>
              <div className={styles.hero__focus_badges}>
                {heroData.focusAreas.map((area, index) => (
                  <span key={index} className={styles.hero__focus_badge}>{area}</span>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className={styles.hero__status_line}>
              <span className={styles.hero__status_indicator}></span>
              <span className={styles.hero__status_text}>{heroData.status}</span>
              <button 
                className={styles.hero__connect_btn}
                onClick={() => {
                  document.getElementById('contact')?.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }}
              >
                Connect
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Decorative accent line bottom */}
      <motion.div
        className={`${styles.hero__accent_line} ${styles.hero__line_bottom}`}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, delay: 0.7, ease: 'easeOut' }}
        style={{ originX: 1 }}
      />
    </section>
  );
};

export default Hero;
